import { useId } from "react";
import type { Filter } from "../../types/experiment";
import { getFilterTypeName, getFilterTypeKey } from "../../types/experiment";
import { getFilterComponent, getFilterData, setFilterData } from "../Filters/FilterRegistry";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { GripVertical, X } from "lucide-react";

interface FilterEditorProps {
  filter: Filter;
  onChange: (filter: Filter) => void;
  onRemove: () => void;
}

export function FilterEditor({ filter, onChange, onRemove }: FilterEditorProps) {
  const id = useId();
  const FilterComponent = getFilterComponent(filter);
  const typeName = getFilterTypeName(filter);
  const negate = (filter as any).negate ?? false;

  const handleDataChange = (data: any) => {
    onChange(setFilterData(filter, data));
  };

  const handleNegateChange = (checked: boolean) => {
    const key = getFilterTypeKey(filter);
    const data = getFilterData(filter);
    onChange({ [key]: data, negate: checked } as Filter);
  };

  return (
    <div className="border border-surface1 rounded-md bg-surface0/30">
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 text-overlay0 cursor-grab" />
          <Label className="text-xs font-medium text-mauve">{typeName}</Label>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <Checkbox
              id={`${id}-negate`}
              checked={negate}
              onCheckedChange={(checked) => handleNegateChange(!!checked)}
            />
            <label htmlFor={`${id}-negate`} className="text-xs text-overlay0 cursor-pointer">negate</label>
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="h-6 w-6">
            <X className="h-3 w-3 text-red" />
          </Button>
        </div>
      </div>
      {FilterComponent && (
        <div className="px-3 pb-3">
          <FilterComponent value={getFilterData(filter)} onChange={handleDataChange} />
        </div>
      )}
    </div>
  );
}
